import { Injectable } from '@angular/core';
import { ApplicationService } from '../application/application.service';
import { DirectEntryStepEnum } from '../../../direct-entry/shared/constants/direct-entry-steps';
import { map, Observable, switchMap } from 'rxjs';
import { ApplicationStateService } from '../application-state/application-state.service';
import { ApplicationSteps } from '../../constants/application-steps';
import { StepLink } from '../../models/step-link';

@Injectable({
    providedIn: 'root',
})
export class DirectEntryStepService {
    constructor(
        private readonly applicationService: ApplicationService,
        private readonly applicationStateService: ApplicationStateService,
    ) {}

    /**
     * Retrieves the Direct Entry Step Links for a given application ID.
     * Steps vary based on the application purpose and template status.
     * @param applicationId The ID of the application.
     * @returns An observable that emits an array of StepLink objects.
     */
    getDirectEntryStepLinksForApplication(
        applicationId: string,
    ): Observable<StepLink[]> {
        return this.getDirectEntryStepsForApplication(applicationId).pipe(
            map((directEntryStepsForApplication: DirectEntryStepEnum[]) => {
                const directEntryStepLinkOptions =
                    ApplicationSteps.getDirectEntryStepLinkOptions(
                        applicationId,
                    );

                const directEntryStepLinksForApplication =
                    directEntryStepLinkOptions.filter((step) =>
                        directEntryStepsForApplication.includes(step.id),
                    );

                return directEntryStepLinksForApplication;
            }),
        );
    }

    /**
     * Retrieves the Direct Entry Steps for an application with special handling based on template status.
     *
     * This method:
     * 1. Fetches the application state to determine template status
     * 2. Retrieves the base Direct Entry Steps for the application
     * 3. Conditionally modifies the steps list based on template status
     *
     * @param applicationId The unique identifier of the application
     * @returns An Observable that emits an array of DirectEntryStepEnum values
     *          - For template applications: Returns the original steps
     *          - For non-template applications: Appends the SubmitToEwa step to the steps list
     */
    private getDirectEntryStepsForApplication(
        applicationId: string,
    ): Observable<DirectEntryStepEnum[]> {
        return this.applicationService.getApplicationState(applicationId).pipe(
            switchMap(() =>
                this.applicationService.getDirectEntrySteps(applicationId),
            ),
            map((steps) => {
                return this.applicationStateService.IsApplicationTemplate()
                    ? steps
                    : [...steps, DirectEntryStepEnum.SubmitToEwa];
            }),
        );
    }
}
